import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography } from 'common/typography';

import { DiscountedItem } from '../../../hooks/useDiscountedItems';

export type ProductCardProps = {
  product?: DiscountedItem;
  banner?: React.ReactNode;
};

const StyledRoot = styled.div`
  border-radius: 24px;
  margin: 0 16px 16px;
  box-shadow: 0 0 12px 0 ${cssThemedValue({ light: colors.pepper12, dark: colors.salt12 })};
  overflow: hidden;
`;

const StyledEmptyMessage = styled.div`
  padding: 72px 16px;
  text-align: center;
  color: ${cbSemanticCssVars.textSecondary};
`;

const StyledImage = styled.img`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 192px;
  object-fit: cover;
  background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
`;

const StyledContentWrapper = styled.div`
  padding: 12px 12px 0;
`;

const StyledName = styled(Typography.Heading4)`
  font-weight: 700;
  margin-block-end: 8px;
`;

const StyledPrice = styled(Typography.Body3)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.wolt100};
  margin-block-end: 12px;
`;

const StyledDescription = styled.div`
  margin-block-end: 12px;
`;

const StyledParagraph = styled.p`
  margin: 0;
  display: inline;
`;

const StyledReadMoreButton = styled.button`
  background: transparent;
  padding: 0;
  color: ${colors.wolt100};
  border: none;
  display: inline;
  cursor: pointer;
`;

const StyledBannerWrapper = styled.div`
  margin: 0 12px 12px;
`;

const MAX_DESCRIPTION_LENGTH = 150;

const ProductCard = ({ product, banner }: ProductCardProps) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const description = product?.description || '';
  const isDescriptionOverflowing = description.length > MAX_DESCRIPTION_LENGTH;
  const trimmedDescription =
    isDescriptionOverflowing && !isDescriptionExpanded
      ? `${description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
      : description;

  return (
    <StyledRoot>
      {!product ? (
        <StyledEmptyMessage>
          <Typography.Body3>
            <FormattedMessage id="campaigns.sidebar-preview.product.empty-state" />
          </Typography.Body3>
        </StyledEmptyMessage>
      ) : (
        <>
          {product.image && <StyledImage src={product.image || ''} alt={product.name} />}
          <StyledContentWrapper>
            <StyledName>{product.name}</StyledName>
            <StyledPrice>
              <MonetaryAmount amount={Number(product.baseprice.amount)} currency={product.baseprice.currency} />
            </StyledPrice>

            {trimmedDescription && (
              <StyledDescription>
                <StyledParagraph>{trimmedDescription}</StyledParagraph>
                {isDescriptionOverflowing && (
                  <StyledReadMoreButton onClick={() => setIsDescriptionExpanded(prevState => !prevState)}>
                    &nbsp;
                    {isDescriptionExpanded ? (
                      <FormattedMessage id="campaigns.sidebar-preview.product.show-less" />
                    ) : (
                      <FormattedMessage id="campaigns.sidebar-preview.product.show-more" />
                    )}
                  </StyledReadMoreButton>
                )}
              </StyledDescription>
            )}
          </StyledContentWrapper>
        </>
      )}

      {<StyledBannerWrapper>{banner}</StyledBannerWrapper>}
    </StyledRoot>
  );
};

export default ProductCard;
